import { memo, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Button,
  TextField,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import toast from "react-hot-toast";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import axios from "axios";
import { useDispatch } from "react-redux";
import { showModal, hideConfirmModal } from "../../redux/counterSlice";

// ✅ Validation Schema
const schema = yup.object().shape({
  shape_name: yup.string().required("Shape name is required"),
  aspect_ratio: yup.string().required("Aspect ratio is required"),
});

const AddNewShape = ({ open, handleClose, loadInitialData, selectedRow }) => {
  const dispatch = useDispatch();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      shape_name: "",
      aspect_ratio: "",
    },
  });

  useEffect(() => {
    if (open) {
      if (selectedRow) {
        reset({
          shape_name: selectedRow.shape_name || "",
          aspect_ratio: selectedRow.aspect_ratio || "",
        });
      } else {
        reset();
      }
    }
  }, [open, reset, selectedRow]);

  const onSubmit = async (data) => {
    try {
      const payload = {
        shape_name: data.shape_name,
        aspect_ratio: data.aspect_ratio,
      };

      let res;
      if (selectedRow?.shape_id) {
        //  Edit mode
        res = await axios.put(`/shape/${selectedRow.shape_id}`, {
          ...payload,
          active_status: selectedRow.active_status, // preserve status
        });
      } else {
        //  Add mode
        res = await axios.post("/shape", payload);
      }

      if (res?.status === 200 || res?.status === 201) {
        toast.success(
          selectedRow?.shape_id
            ? "Shape updated successfully"
            : "Shape created successfully"
        );
        loadInitialData();
        reset();
        handleClose();
      } else {
        dispatch(
          showModal({
            msg: res?.data?.message || "Unknown error",
            error: true,
          })
        );
      }
    } catch (err) {
      console.error("Shape save error:", err.response?.data || err.message);
      toast.error(err.response?.data?.message || "Failed to save shape");
    } finally {
      dispatch(hideConfirmModal());
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle className="text-primary"> {selectedRow ? "Edit Shape" : "Add New Shape"} </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Controller
              name="shape_name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Shape Name"
                  fullWidth
                  error={!!errors.shape_name}
                  helperText={errors.shape_name?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="aspect_ratio"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Aspect Ratio (e.g. 1:1)"
                  fullWidth
                  error={!!errors.aspect_ratio}
                  helperText={errors.aspect_ratio?.message}
                />
              )}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="error" variant="contained">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          color="primary"
          variant="contained"
          startIcon={<SaveIcon />}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(AddNewShape);
